import React, { FC, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FollowUpEmails,
  OrganizationManagementRecord,
  OrganizationSetup,
  SftpAccess,
  SupplierRegistrationPortal,
} from '@app/generated/svm';

interface OrganizationSetupCardsProps {
  organizationSetup: OrganizationSetup;
}

const OrganizationSetupCards: FC<OrganizationSetupCardsProps> = ({ organizationSetup }) => {
  return (
    <div className='grid grid-cols-1 gap-4 md:grid-cols-2' data-testid='organization-setup-cards'>
      <SetupCard title='Organization management' dataTestId='organization-setup-organization-management'>
        {renderOrganizationManagementRecord(organizationSetup.organizationManagementRecord)}
      </SetupCard>
      <SetupCard title='Supplier registration portal' dataTestId='organization-setup-portal'>
        {renderPortal(organizationSetup.supplierRegistrationPortal)}
      </SetupCard>
      <SetupCard title='Follow-up emails' dataTestId='organization-setup-follow-up-emails'>
        <SetupLine>{toFollowUpEmailsText(organizationSetup.followUpEmails)}</SetupLine>
      </SetupCard>
      <SetupCard title='SFTP access' dataTestId='organization-setup-sftp-access'>
        {renderSftpAccess(organizationSetup.sftpAccess)}
      </SetupCard>
    </div>
  );
};

export default OrganizationSetupCards;

function renderOrganizationManagementRecord(record: OrganizationManagementRecord | null): ReactNode {
  if (!record) {
    return <SetupLine>No record: the validation system doesn’t know this organization yet</SetupLine>;
  }

  return (
    <>
      <SetupLine>
        {record.usesSupplierValidation
          ? 'Uses supplier validation: listed in the validation system'
          : 'Doesn’t use supplier validation: not listed in the validation system'}
      </SetupLine>
      <SetupLine>Group: {record.groupName ?? 'None'}</SetupLine>
    </>
  );
}

function renderPortal(portal: SupplierRegistrationPortal | null): ReactNode {
  if (!portal) {
    return <SetupLine>No portal</SetupLine>;
  }

  return (
    <>
      <SetupLine>Subdomain: {portal.subdomain ?? 'None'}</SetupLine>
      <SetupLine>Reference id: {portal.referenceId}</SetupLine>
      <SetupLine>{portal.disabled ? 'Turned off' : 'Turned on'}</SetupLine>
      <SetupLine>{portal.branded ? 'Branded' : 'Not branded'}</SetupLine>
      <SetupLine>Finicity open banking: {toFinicityText(portal.useFinicityOpenBanking)}</SetupLine>
    </>
  );
}

function toFinicityText(useFinicityOpenBanking: boolean | null): string {
  if (useFinicityOpenBanking === null) {
    return 'Environment default';
  }

  return useFinicityOpenBanking ? 'On' : 'Off';
}

function toFollowUpEmailsText(followUpEmails: FollowUpEmails | null): string {
  if (!followUpEmails) {
    return 'The follow-up email sender isn’t configured in this environment.';
  }

  if (followUpEmails.runOnAllOrganizations) {
    return followUpEmails.organizationIncluded
      ? 'Sent: the sender runs on every organization it doesn’t exclude, and it doesn’t exclude this one.'
      : 'Not sent: the sender runs on every organization it doesn’t exclude, and it excludes this one.';
  }

  return followUpEmails.organizationIncluded
    ? 'Sent: the sender runs only on the organizations it lists, and it lists this one.'
    : 'Not sent: the sender runs only on the organizations it lists, and it doesn’t list this one.';
}

function renderSftpAccess(sftpAccess: SftpAccess): ReactNode {
  return (
    <>
      <SetupLine>File-based SFTP: {sftpAccess.fileBasedSftp ? 'Yes' : 'No'}</SetupLine>
      <SetupLine>External validation SFTP: {sftpAccess.externalValidationSftp ? 'Yes' : 'No'}</SetupLine>
      <SetupLine className='text-muted-foreground'>Granted as organization services in Backee</SetupLine>
    </>
  );
}

const SetupCard: FC<{ title: string; dataTestId: string; children: ReactNode }> = ({ title, dataTestId, children }) => (
  <Card data-testid={dataTestId}>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
    </CardHeader>
    <CardContent className='flex flex-col gap-1'>{children}</CardContent>
  </Card>
);

const SetupLine: FC<{ className?: string; children: ReactNode }> = ({ className, children }) => (
  <div className={className}>{children}</div>
);
